import { useState, useRef, useCallback } from 'react';
import { Box, Button, Stack, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconCamera, IconRefresh, IconCheck } from '@tabler/icons-react';

interface BetSlipCameraCaptureProps {
  onCapture: (imageBlob: Blob) => void;
}

export function BetSlipCameraCapture({ onCapture }: BetSlipCameraCaptureProps) {
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [hasCapture, setHasCapture] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const startCamera = useCallback(async () => {
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: 'environment', // Prefer back camera on mobile
          width: { ideal: 1920 },    // Request HD resolution
          height: { ideal: 1080 },
          aspectRatio: { ideal: 4/3 }
        },
        audio: false,
      });
      setStream(mediaStream);
      
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
      }
    } catch (error) {
      console.error('Camera access error:', error);
      notifications.show({
        title: 'Error',
        message: 'Failed to access camera. Please ensure camera permissions are granted.',
        color: 'red',
      });
    }
  }, []);

  const stopCamera = useCallback(() => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  }, [stream]);

  const captureImage = useCallback(() => {
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');
    if (!context) return;

    // Set canvas dimensions to match video
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    // Draw the current video frame
    context.drawImage(video, 0, 0, canvas.width, canvas.height);

    // Convert to blob with higher quality and resolution
    const maxDimension = Math.max(video.videoWidth, video.videoHeight);
    const scale = maxDimension < 1920 ? 1 : 1920 / maxDimension; // Target 1920px max dimension
    
    // Create a temporary canvas for scaling
    const tempCanvas = document.createElement('canvas');
    tempCanvas.width = video.videoWidth * scale;
    tempCanvas.height = video.videoHeight * scale;
    const tempContext = tempCanvas.getContext('2d');
    
    if (tempContext) {
      // Apply image processing for better OCR
      tempContext.fillStyle = '#FFFFFF';
      tempContext.fillRect(0, 0, tempCanvas.width, tempCanvas.height);
      tempContext.drawImage(video, 0, 0, tempCanvas.width, tempCanvas.height);
      
      // Increase contrast
      tempContext.globalCompositeOperation = 'source-over';
      tempContext.globalAlpha = 0.2;
      tempContext.drawImage(tempCanvas, 0, 0);
      tempContext.globalAlpha = 1.0;
      
      // Convert to blob with high quality
      tempCanvas.toBlob((blob) => {
        if (blob) {
          setHasCapture(true);
          onCapture(blob);
        }
      }, 'image/jpeg', 0.95); // Higher quality
    }
  }, [onCapture]);

  const retake = useCallback(() => {
    setHasCapture(false);
  }, []);

  // Clean up on unmount
  const cleanup = useCallback(() => {
    stopCamera();
  }, [stopCamera]);

  return (
    <Stack spacing="md" align="center">
      <Box
        sx={{
          width: '100%',
          maxWidth: 600,
          position: 'relative',
          aspectRatio: '4/3',
          backgroundColor: '#000',
          borderRadius: 8,
          overflow: 'hidden',
        }}
      >
        {/* Overlay guide for better photo alignment */}
        {stream && !hasCapture && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 1,
              pointerEvents: 'none',
              border: '2px solid rgba(255, 255, 255, 0.5)',
              margin: '20px',
            }}
          >
            <Text
              sx={{
                position: 'absolute',
                top: '10px',
                left: '50%',
                transform: 'translateX(-50%)',
                color: 'white',
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                padding: '4px 8px',
                borderRadius: '4px',
                fontSize: '14px',
              }}
            >
              Align bet slip within the frame
            </Text>
          </Box>
        )}
        {!stream && !hasCapture && (
          <Stack spacing="xs" align="center" sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}>
            <Text size="sm" color="white" align="center" sx={{ maxWidth: 300, mb: 'md' }}>
              Tips for best results:
              • Hold the camera steady
              • Ensure good lighting
              • Keep bet slip straight and flat
              • Include all text clearly in frame
            </Text>
            <Button
              leftIcon={<IconCamera size="1.2rem" />}
              onClick={startCamera}
            >
              Start Camera
            </Button>
          </Stack>
        )}

        <video
          ref={videoRef}
          autoPlay
          playsInline
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            display: stream && !hasCapture ? 'block' : 'none',
          }}
        />

        <canvas
          ref={canvasRef}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            display: hasCapture ? 'block' : 'none',
          }}
        />
      </Box>

      {stream && !hasCapture && (
        <Button
          leftIcon={<IconCamera size="1.2rem" />}
          onClick={captureImage}
          size="lg"
        >
          Take Photo
        </Button>
      )}

      {hasCapture && (
        <Stack spacing="xs">
          <Text size="sm" align="center">Photo captured! Review the image or take another.</Text>
          <Button.Group>
            <Button
              variant="light"
              leftIcon={<IconRefresh size="1.2rem" />}
              onClick={retake}
            >
              Retake Photo
            </Button>
            <Button
              leftIcon={<IconCheck size="1.2rem" />}
              onClick={cleanup}
            >
              Use Photo
            </Button>
          </Button.Group>
        </Stack>
      )}
    </Stack>
  );
}